import { type FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { css } from "@stubhub/broadway-foundation/css";
import { Accordion, AccordionContent, AccordionHeader, Divider, Heading } from "@stubhub/broadway-web";
import { Content, useContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { seoSectionRecipe } from "src/website-apps/shared/components/seo-content";
import { type FaqItem, parseFaqItems } from "./parseFaqItems";

// Generic FAQ CMS blob shared with SeoContentV2's includeAgnosticText.
// Body: Q&A HTML — "Where can I buy {performer} tickets?", "How do I sell {performer} tickets?", …
const GENERIC_FAQ_CONTENT_ID = "cid$#3320059#";

// Below this count the blob is treated as unparseable and rendered raw (current behavior).
const MIN_PARSED_ITEMS = 2;

const faqList = css({ display: "flex", flexDirection: "column" });

const flushAccordionHeader = css({
  "&[aria-expanded]": { paddingInlineStart: "primitives.0" },
  "&:hover": { backgroundColor: "[transparent]" },
  "&:active": { backgroundColor: "[transparent]" },
  // Toggle text, not content — matches Broadway's tabs recipe; answers stay selectable (sibling, not child).
  userSelect: "[none]",
});

// Broadway bakes the scale/height reveal and content inline padding into its Accordion recipe with no opt-out
// props — this instance toggles instantly and aligns with the page gutter.
const accordionContentFlush = css({
  "&[data-expanded]": {
    transitionProperty: "[none]",
    paddingInlineStart: "primitives.0",
    paddingInlineEnd: "primitives.0",
  },
});

// The Divider is the rule above the first question — its bottom margin must not stack with the
// first row's header padding (only its top margin separates it from the bio above).
const bioDividerFlush = css({ marginBlockEnd: "primitives.0" });

interface ItemProps {
  index: number;
  question: string;
  answer: string;
  defaultExpanded: boolean;
  onToggle: (index: number) => void;
}

const FaqAccordionItem: FC<ItemProps> = memo(({ index, question, answer, defaultExpanded, onToggle }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // React 18 drops boolean inert, so AccordionContent's collapsed subtree stays tabbable — apply it
  // imperatively on a wrapper until the app is on React 19 (where Broadway's inert={true} works as-is).
  const answerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    answerRef.current?.toggleAttribute("inert", !expanded);
  }, [expanded]);

  const handleToggle = useCallback(() => {
    setExpanded((previous) => !previous);
    onToggle(index);
  }, [index, onToggle]);

  return (
    <Accordion variant="divider" expanded={expanded} onToggle={handleToggle}>
      {/* Question and answer are CMS HTML; DOMPurify-sanitized before injection. */}
      {/* AccordionHeader renders a div[role="button"], so an h3 child is valid flow content —
          mirrors the About accordion's h2 for the crawler-facing outline; don't revert to a span. */}
      <AccordionHeader classes={{ root: flushAccordionHeader }}>
        <Heading
          as="h3"
          variant="title"
          size="medium"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question) }}
        />
      </AccordionHeader>
      <div ref={answerRef}>
        <AccordionContent className={accordionContentFlush}>
          <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(answer) }} />
        </AccordionContent>
      </div>
    </Accordion>
  );
});

FaqAccordionItem.displayName = "FaqAccordionItem";

interface Props {
  /** Bio / CMS intro content renders above the FAQ; when present a horizontal Divider separates it. */
  hasBio?: boolean;
  /** Bots get fully expanded items in server-rendered HTML (mirrors the outer accordion's renderContext). */
  expandAll?: boolean;
  /** Performer-specific Q&A from the CMS bottom body, rendered as the first accordion rows. */
  performerItems?: FaqItem[] | undefined;
  /** Server-resolved tour FAQ items, rendered before performer and generic rows. */
  tourItems?: FaqItem[];
  /** Whether the generic FAQ arm is on for this user; the tour FAQ mounts this component independently, so its rows are suppressed when that arm is off. */
  showGenericItems?: boolean;
}

export const FaqAccordion: FC<Props> = memo(
  ({ hasBio = false, expandAll = false, performerItems, tourItems, showGenericItems = true }) => {
    const rawFaq = useContent(GENERIC_FAQ_CONTENT_ID);
    const genericItems = useMemo(() => parseFaqItems(rawFaq), [rawFaq]);
    const storePageEvent = useStorePageEvent();

    const handleItemToggle = useCallback(
      (index: number) => {
        storePageEvent(PE_TYPE.CLICK, PE_KEYS.PERFORMER_GENERIC_FAQ_ITEM_TOGGLE, String(index));
      },
      [storePageEvent],
    );

    const handleTourItemToggle = useCallback(
      (index: number) => {
        storePageEvent(PE_TYPE.CLICK, PE_KEYS.PERFORMER_TOUR_FAQ_ITEM_TOGGLE, String(index));
      },
      [storePageEvent],
    );

    const sectionClassName = seoSectionRecipe({
      surface: "transparent",
      topPadding: "flush",
      bottomPadding: "flush",
    });

    const genericUnparseable = genericItems.length < MIN_PARSED_ITEMS;
    const hasPerformerItems = (performerItems?.length ?? 0) > 0;
    const hasTourItems = (tourItems?.length ?? 0) > 0;
    const showGenericRaw = showGenericItems && genericUnparseable;
    const showGenericRows = showGenericItems && !genericUnparseable;

    if (!hasTourItems && !hasPerformerItems && !showGenericRows && !showGenericRaw) {
      return null;
    }

    if (!hasTourItems && !hasPerformerItems && showGenericRaw) {
      return (
        <section className={sectionClassName}>
          <Content id={GENERIC_FAQ_CONTENT_ID} raw />
        </section>
      );
    }

    return (
      <>
        {hasBio && <Divider spacing="primitives.250" classes={{ root: bioDividerFlush }} />}
        <section className={sectionClassName}>
          <div className={faqList}>
            {(tourItems ?? []).map((item, index) => (
              <FaqAccordionItem
                key={`tour-${index}`}
                index={index}
                question={item.question}
                answer={item.answer}
                defaultExpanded={expandAll}
                onToggle={handleTourItemToggle}
              />
            ))}
            {(performerItems ?? []).map((item, index) => (
              <FaqAccordionItem
                key={`performer-${index}`}
                index={(tourItems?.length ?? 0) + index}
                question={item.question}
                answer={item.answer}
                defaultExpanded={expandAll}
                onToggle={handleItemToggle}
              />
            ))}
            {showGenericRows &&
              genericItems.map((item, index) => (
                <FaqAccordionItem
                  key={`generic-${index}`}
                  index={(tourItems?.length ?? 0) + (performerItems?.length ?? 0) + index}
                  question={item.question}
                  answer={item.answer}
                  defaultExpanded={expandAll}
                  onToggle={handleItemToggle}
                />
              ))}
          </div>
        </section>
        {showGenericRaw && (
          <section className={sectionClassName}>
            <Content id={GENERIC_FAQ_CONTENT_ID} raw />
          </section>
        )}
      </>
    );
  },
);

FaqAccordion.displayName = "FaqAccordion";
